import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { textRegExp } from '../../constants/regularExpressions';
import { serverUtils } from '../../utils/serverUtils/serverUtils';

export type RouterBuildHooks = {
  useCOPNextParams(): COPNextParams | null;
  useGetUrlParam(): { urlParam: string };
  useGoBack(): () => void;
  useLOPProductParams(): string;
  usePageParams<T,>(): T;
  usePathName(): string;
  useRedirect(redirect: string): () => void;
  useRedirectCallback(): (redirect: string) => void;
};

export type COPNextParams = {
  slug?: string[];
};

export type RouteParams = {
  language?: string;
};

export type LOPProductParams = {
  product: string;
};

export const nextRouterBuildHooks: RouterBuildHooks = {
  usePageParams<T,>() {
    const router = require('next/router').useRouter();
    return router.query as T;
  },

  useCOPNextParams() {
    const router = require('next/router').useRouter();
    return router.query as COPNextParams;
  },

  useGetUrlParam() {
    const router = require('next/router').useRouter();
    const position = router.asPath.indexOf(textRegExp);
    return React.useMemo(() => {
      return {
        urlParam: position !== -1 ? decodeURI(router.asPath
          .slice(position)
          .replace(textRegExp, '')) : '',
      };
    }, [position, router.asPath]);
  },

  useRedirect(redirect) {
    const router = require('next/router').useRouter();
    return React.useCallback(() => {
      router.push(redirect);
    }, [redirect, router]);
  },

  useRedirectCallback() {
    const router = require('next/router').useRouter();
    return React.useCallback((redirect) => {
      router.push(redirect);
    }, [router]);
  },

  usePathName() {
    const router = require('next/router').useRouter();

    return router.asPath;
  },

  useLOPProductParams() {
    const router = require('next/router').useRouter();
    return router.query.slug;
  },

  useGoBack() {
    const router = require('next/router').useRouter();
    return React.useCallback(() => {
      router.back();
    }, [router]);
  },
};

export const reactRouterBuildHooks: RouterBuildHooks = {
  usePageParams<T,>() {
    const params = useParams() as T;
    return params;
  },

  useGetUrlParam() {
    return React.useMemo(() => {
      return {
        urlParam: decodeURI(window.location.hash.replace(textRegExp, '')),
      };
    }, []);
  },

  useRedirect(redirect) {
    const history = useHistory();
    return React.useCallback(() => {
      history.push(redirect);
    }, [history, redirect]);
  },

  useRedirectCallback() {
    const history = useHistory();
    return React.useCallback((redirect) => {
      history.push(redirect);
    }, [history]);
  },

  usePathName() {
    const location = useLocation();
    return location.pathname;
  },

  useCOPNextParams() {
    return null;
  },

  useLOPProductParams() {
    const params = useParams() as LOPProductParams;
    return params.product;
  },

  useGoBack() {
    const history = useHistory();
    return React.useCallback(() => {
      history.goBack();
    }, [history]);
  },
};

export const routerBuildHooks: RouterBuildHooks =
  /* istanbul ignore next */
  serverUtils.isNextBuild() ? nextRouterBuildHooks : reactRouterBuildHooks;
